import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash'

import { landAxios } from '@Root/utils/landAxios'
import MyhouseMainHouseResponse from '@Types/entities/MyhouseMainHouseResponse'
import { FETCH_STATUS } from '@Root/contants'
import FetchResponse from '@Types/inner/FetchResponse'

export const fetchHouse = createAsyncThunk('/myhouse/auth/', async (myhouseNo: number) => {
  return await (await landAxios.get(`/myhouse/v2/${myhouseNo}`)).data
})

export const fetchNpayAuthentication = createAsyncThunk(
  'myhouse/postNpayAuthentication',
  async (myhouseNo: number) => await (await landAxios.post(`/myhouse/authentication/npay/${myhouseNo}`)).data,
)

export const cancelAuthentication = createAsyncThunk(
  'myhouse/cancelAuthentication',
  async (myhouseNo: number) => await (await landAxios.delete(`/myhouse/authentication/${myhouseNo}`)).data,
)

export const cancelAuthenticationCount = createAsyncThunk(
  'myhouse/cancelAuthenticationCount',
  async (myhouseNo: number) => await (await landAxios.delete(`/myhouse/authentication/count/${myhouseNo}`)).data,
)

export type MyHouseAuthState = FetchResponse<MyhouseMainHouseResponse> & {
  agreeCollectTerms: boolean
  authApplyNotifyYn: 'Y' | 'N'
  npayAuthentication: FetchResponse<{ result: boolean }>
}

const initialState: MyHouseAuthState = {
  agreeCollectTerms: false,
  authApplyNotifyYn: 'Y',
  npayAuthentication: {},
} as MyHouseAuthState

const myHouseAuthenticationSlice = createSlice({
  name: 'myhouse',
  initialState,
  reducers: {
    updateAgreeCollectTerms: (state: MyHouseAuthState, action: PayloadAction<boolean>) => {
      state.agreeCollectTerms = action.payload
    },
    updateAuthApplyNotifyYn: (state: MyHouseAuthState, action: PayloadAction<'Y' | 'N'>) => {
      state.authApplyNotifyYn = action.payload
    },
  },
  extraReducers: {
    [fetchHouse.fulfilled.type]: (state: MyHouseAuthState, action: PayloadAction<MyhouseMainHouseResponse>) => {
      Object.assign(state, action.payload)

      state.ownerAuthStatus = state.ownerAuthStatus || 'INIT'
      state.residentAuthStatus = state.residentAuthStatus || 'INIT'
      state.ownerAuthCnt = state.ownerAuthCnt || 0
      state.residentAuthCnt = state.residentAuthCnt || 0

      if (!action.payload || isEmpty(action.payload)) {
        state.fetchStatus = FETCH_STATUS.FAILURE
      } else {
        state.fetchStatus = FETCH_STATUS.SUCCESS
      }
    },
    [fetchHouse.pending.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchHouse.rejected.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },
    [cancelAuthentication.fulfilled.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [cancelAuthentication.pending.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [cancelAuthentication.rejected.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },
    [cancelAuthenticationCount.fulfilled.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [cancelAuthenticationCount.pending.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [cancelAuthenticationCount.rejected.type]: (state: MyHouseAuthState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchNpayAuthentication.fulfilled.type]: (state: MyHouseAuthState, action: PayloadAction<boolean>) => {
      state.npayAuthentication.result = action.payload
      state.npayAuthentication.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchNpayAuthentication.pending.type]: (state: MyHouseAuthState) => {
      state.npayAuthentication.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchNpayAuthentication.rejected.type]: (state: MyHouseAuthState) => {
      state.npayAuthentication.fetchStatus = FETCH_STATUS.FAILURE
    },
  },
})

export const { updateAgreeCollectTerms, updateAuthApplyNotifyYn } = myHouseAuthenticationSlice.actions
export default myHouseAuthenticationSlice.reducer
